/**
 * Die Farben der Lichter. Eine Quelle für die Szene und für die Oberfläche.
 *
 * Vorher standen sie zweimal im Code: als Zahlen in `src/render/lights.ts` und als Marken
 * in `src/ui/styles.css`. Beide Stellen meinten dasselbe und sagten Verschiedenes, und der
 * Kommentar über den Marken behauptete "dieselben wie in der Szene", was nicht stimmte
 * (weiß war im Bild warm und in der Liste kühl). Für eine App, die Lichter lehrt, ist das
 * kein Schönheitsfehler: der Punkt neben der Beschreibung soll genau die Farbe zeigen, die
 * der Nutzer gleich in der Szene sucht.
 *
 * ## Woher die Werte kommen
 *
 * Anlage I Abschnitt 7 (im Bestand `101 s7:b(i..iv)`) gibt für jede Farbe ein Gebiet in der
 * Normfarbtafel der CIE an, als Eckpunkte in x und y. Das ist eine Fläche, kein Punkt: die
 * Regel schreibt vor, wo eine Laterne liegen darf, nicht welchen Farbwert ein Bildschirm
 * anzeigen soll. Die Werte hier sind deshalb gewählt und nicht gerechnet, aber sie sind an
 * das Gebiet gebunden: `test/lichtfarben.test.ts` rechnet jeden Wert in Farbörter um und
 * verlangt, dass er dem Gebiet seiner eigenen Farbe näher liegt als jedem anderen. Ein
 * Grün, das ins Gelbe rutscht, fällt damit auf.
 *
 * Warum nicht direkt den Schwerpunkt des Gebiets nehmen und umrechnen: die Gebiete für Rot
 * und Grün liegen außerhalb dessen, was ein sRGB-Bildschirm darstellen kann, und das Gebiet
 * für Weiß reicht von Tageslichtweiß bis tief ins Warme. Rechnet man den Schwerpunkt stur
 * um und beschneidet auf den Farbraum, kommt für Weiß ein Orange (#ffaf80) und für Gelb ein
 * Rotorange (#ff6500) heraus. Das wäre formal hergeleitet und in der Sache falsch, weil der
 * Nutzer die Farben nicht einzeln, sondern nebeneinander sieht und sein Auge sich an die
 * hellste anpasst.
 *
 * ## Was in den drei Ansichten gilt
 *
 * Nacht und Tag zeigen dieselben Werte: das Licht ändert seine Farbe nicht, weil daneben
 * eine helle Fläche liegt. Im Tagmodus bekommt der Punkt einen dunklen Rand, damit ein
 * weißes Licht auf weißem Grund sichtbar bleibt.
 *
 * Im Rotmodus legt die Szene den Nachtsichtfilter über das Bild
 * (`src/render/nightVision.ts`): die Helligkeit wandert in den Rotkanal, Grün und Blau
 * werden null. Die Marken im Rotmodus sind deshalb ebenfalls reines Rot, und ihre Rangfolge
 * ist die, die der Filter erzeugt (Weiß am hellsten, dann Gelb, Grün, Rot). Angehoben sind
 * sie nur so weit, dass sie den Kontrast auf der Fläche halten; die Reihenfolge bleibt.
 */
import type { LightColour } from './types'

/** Die Farbe eines Lichts, wie Szene und Liste sie zeigen. */
export const LICHTFARBE: Record<LightColour, string> = {
  white: '#fff4e0',
  red: '#ff2a1c',
  green: '#18f06a',
  yellow: '#ffd21a',
  /**
   * Blau führt kein Zustand des Bestands: das blaue Funkellicht ist eine Sache der
   * US-Binnenregeln für Behördenfahrzeuge. Der Wert steht hier, weil der Typ ihn kennt.
   */
  blue: '#3aa0ff',
}

/** Fundstelle der Farbgebiete, für die Prüfung und für die Anzeige. */
export const LICHTFARBE_QUELLE = '101 s7:b'

/** Derselbe Wert als Zahl, wie three.js ihn erwartet. */
export function alsZahl(farbe: LightColour): number {
  return Number.parseInt(LICHTFARBE[farbe].slice(1), 16)
}

/** Ein Hexwert als drei Kanäle von 0 bis 255. */
export function kanaele(hex: string): [number, number, number] {
  const wert = hex.trim()
  if (!/^#[0-9a-fA-F]{6}$/.test(wert)) throw new Error(`keine Farbe: ${hex}`)
  return [
    Number.parseInt(wert.slice(1, 3), 16),
    Number.parseInt(wert.slice(3, 5), 16),
    Number.parseInt(wert.slice(5, 7), 16),
  ]
}
